import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EvidenceEntry, ExceptionEntry, ActivityEntry } from '@/api/types/cts-tasks.types';
import {
  captureActor, changeLabelFor, unavailableKind,
} from '@/components/compliance/evidence-copy';

// ── TC4 — the Evidence, Exceptions and Activity tabs of the unified workspace ────────────────

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtStamp = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function ControlLink({ reference, code, title }: { reference: string; code: string; title: string }) {
  return (
    <Link to={`/compliance/workspace/controls/${reference}`}
      className="mt-[4px] flex flex-wrap items-baseline gap-[8px] transition-colors hover:opacity-80">
      <span className="font-mono text-[11.5px] text-accent-cyan">{code}</span>
      <span className="text-[12.5px] text-text-secondary">{title}</span>
    </Link>
  );
}

export function EvidencePanel({ rows }: { rows: EvidenceEntry[] }) {
  return (
    <div className={cn(PANEL, 'overflow-hidden')}>
      {rows.length === 0 ? (
        <p className="px-[16px] py-[20px] text-[13px] italic text-text-faint">
          No evidence captured yet.
        </p>
      ) : rows.map((e) => (
        <div key={e.id} className="border-b border-border-subtle px-[16px] py-[13px] last:border-b-0">
          <div className="flex flex-wrap items-baseline gap-[12px]">
            <span className="min-w-0 flex-1 text-[13.5px] font-medium text-text-primary">{e.title}</span>
            {/*
              ⚠ P1.4 — THE EXPIRY VOCABULARY IS GONE, because the thing it described is gone. This
              register reads the go-forward model, which has NO validity window: "Expired",
              "Valid until" and "No expiry recorded" all describe a column that no longer exists,
              and rendering them over capture dates would be a false statement about a document
              nobody said expires.
              ⓘ The recency ADVICE (age against the catalogue's suggested cadence) is rendered on the
              control's own Evidence tab, which reads the P1.4 endpoint. This register carries the
              capture date alone until it is extended — flagged, not faked.
            */}
          </div>
          <ControlLink reference={e.control_reference} code={e.control_code} title={e.control_title} />
          {/* ⚠ ERI S2 — every artifact stays listed; one that is not current says so, in the existing
              change label for its state (never "re-review required"). */}
          {unavailableKind(e.availability) && (
            <p className="mt-[5px] text-[11.5px] font-medium text-cert-warn-fg" data-testid="register-availability">
              {changeLabelFor(unavailableKind(e.availability)!, e.repo_kind)}
            </p>
          )}
          <p className="mt-[5px] text-[11.5px] text-text-secondary">
            {/* ⚠ ERI S2 C1 — the actor is decided from PRESENCE (D-ERI-7), never from a missing name. */}
            {(() => {
              const who = captureActor(e.captured_by_platform, e.collected_by_name);
              return who.kind === 'named_user'
                ? <>Captured {fmt(e.collected_at)} by {who.name}</>
                : <>Captured {fmt(e.collected_at)} · {who.phrase}</>;
            })()}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * ⚠ THE EXCEPTION STATUS VOCABULARY IS THE STORED ONE, NOT A FRIENDLIER SYNONYM.
 *
 * `cts_exceptions.status` is a FOUR-STATE lifecycle — requested · approved · rejected · withdrawn
 * — with `decided_at`/`decided_by` and a CHECK requiring `decision_reason` when rejected. Design's
 * mock showed "Pending"; the stored value is `requested`, and renaming it on the way out would
 * put a fifth word into a four-word vocabulary that the database, the api and the register would
 * then each have to translate.
 *
 * ⓘ The lifecycle exists in the schema but has no WRITE path yet — TC4 renders the register; the
 * exception-approval workflow is its own slice. So this shows what is recorded and offers no
 * transitions.
 */
const EXCEPTION_LABEL: Record<string, string> = {
  requested: 'Requested', approved: 'Approved', rejected: 'Rejected', withdrawn: 'Withdrawn',
};
const EXCEPTION_TONE: Record<string, string> = {
  requested: 'bg-amber-500/12 text-amber-300',
  approved: 'bg-teal-500/12 text-teal-300',
  rejected: 'bg-red-500/12 text-red-300',
  withdrawn: 'bg-slate-500/12 text-slate-300',
};

export function ExceptionsPanel({ rows }: { rows: ExceptionEntry[] }) {
  return (
    <div className={cn(PANEL, 'overflow-hidden')}>
      {rows.length === 0 ? (
        <p className="px-[16px] py-[20px] text-[13px] italic text-text-faint">
          No exceptions have been raised.
        </p>
      ) : rows.map((x) => (
        <div key={x.id} className="border-b border-border-subtle px-[16px] py-[13px] last:border-b-0">
          <div className="flex flex-wrap items-baseline gap-[12px]">
            <span className="min-w-0 flex-1 text-[13.5px] font-medium text-text-primary">{x.title}</span>
            <span className={cn('shrink-0 rounded-full px-[10px] py-[2px] text-[11.5px] font-medium',
              EXCEPTION_TONE[x.status] ?? 'bg-bg-surface-2 text-text-secondary')}>
              {EXCEPTION_LABEL[x.status] ?? x.status}
            </span>
          </div>
          <ControlLink reference={x.control_reference} code={x.control_code} title={x.control_title} />
          <p className="mt-[6px] max-w-[86ch] text-[12.5px] leading-[1.5] text-text-secondary">{x.justification}</p>
          {/* Expiry is derived at read exactly as evidence freshness is, and for the same reason. */}
          {x.expiry_state === 'lapsed' && (
            <p className="mt-[5px] text-[12px] font-medium text-cert-expired-fg">Expired {fmt(x.expires_at)}</p>
          )}
          {x.expiry_state === 'active' && (
            <p className="mt-[5px] text-[12px] text-text-secondary">Expires {fmt(x.expires_at)}</p>
          )}
          <p className="mt-[5px] text-[11.5px] text-text-faint">
            Requested {fmt(x.requested_at)}{x.requested_by_name ? ` by ${x.requested_by_name}` : ''}
          </p>
        </div>
      ))}
    </div>
  );
}

export function ActivityPanel({ rows }: { rows: ActivityEntry[] }) {
  return (
    <div className={cn(PANEL, 'overflow-hidden')}>
      {rows.length === 0 ? (
        <p className="px-[16px] py-[20px] text-[13px] italic text-text-faint">
          Nothing has happened in your programme yet.
        </p>
      ) : rows.map((a) => (
        <div key={a.id} className="flex items-baseline gap-[14px] border-b border-border-subtle px-[16px] py-[11px] last:border-b-0">
          <span className="min-w-0 flex-1 text-[13px] text-text-primary">{a.summary}</span>
          <span className="shrink-0 text-[11.5px] text-text-secondary">{a.actor_name ?? 'System'}</span>
          <span className="w-[120px] shrink-0 text-right text-[11.5px] text-text-faint">{fmtStamp(a.occurred_at)}</span>
        </div>
      ))}
      {/*
        ⚠ SAYING WHAT THIS IS NOT, IN THE UI, AND NOT AS SMALL PRINT. This is a programme feed —
        mutable and prunable by design. `audit_logs` is the compliance record: append-only by
        privilege, and the thing an auditor reads. Leaving the distinction unstated invites someone
        to cite this page as evidence of what happened, and then to ask for it to be tidied — and a
        stream a tenant can tidy was never an audit trail.
      */}
      <p className="flex items-start gap-[8px] border-t border-border-subtle bg-bg-surface-2/30 px-[16px] py-[11px] text-[11.5px] leading-[1.5] text-text-faint">
        <Info className="mt-[2px] h-[12px] w-[12px] shrink-0" aria-hidden="true" />
        <span>
          <span className={cn(EYEBROW, 'mr-[7px]')}>Not an audit trail</span>
          A record of programme activity for your team. The compliance audit trail is recorded
          separately and cannot be edited.
        </span>
      </p>
    </div>
  );
}
